/* eslint-disable react-hooks/exhaustive-deps */

import { getFilterPromptFragmentsAPI } from "Api/promptFragments";
import Loader from "components/loader/Loader";
import React, { useCallback, useEffect, useState } from "react";
import { palette } from 'theme/tokens';

function PromptFragmentsMacrosList(props) {
  const {
    dropdownPosition,
    fragmentsTableData,
    macroSearch,
    selectedIndex,
    handleMacroSelect,
    setSelectedIndex,
    setFragmentsTableData,
    hideDropDown,
  } = props;

  const [/* isLoading */, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const limit = 4;

  const getPromptFragments = useCallback(async () => {
    if (macroSearch !== "" || isFetching || !hasMore) return;

    setIsFetching(true);
    const token = localStorage.getItem("jwtToken");

    try {
      const response = await getFilterPromptFragmentsAPI(
        { pageIndex: (page - 1) * limit, limit },
        { status: true },
        setIsLoading,
        token
      );

      if (response?.data) {
        setHasMore(response.data.length >= limit);
        setFragmentsTableData((prev) =>
          page === 1 ? response.data : [...prev, ...response.data]
        );
      }
    } catch (error) {
      console.error("Error fetching prompt fragments:", error);
    } finally {
      setIsFetching(false);
    }
  }, [macroSearch, isFetching, hasMore, page, setFragmentsTableData]);

  const getFilteredPromptFragments = useCallback(async () => {
    if (macroSearch === "") return;

    setIsFetching(true);
    const token = localStorage.getItem("jwtToken");

    try {
      const response = await getFilterPromptFragmentsAPI(
        { pageIndex: 0, limit },
        { searchQuery: macroSearch },
        setIsLoading,
        token
      );

      if (response?.data) {
        setFragmentsTableData(response.data);
        if (response.data.length === 0) hideDropDown();
      }
    } catch (error) {
      console.error("Error fetching prompt fragments:", error);
    } finally {
      setIsFetching(false);
    }
  }, [macroSearch, setFragmentsTableData]);

  useEffect(() => {
    setFragmentsTableData([]);
    setHasMore(true);
    setPage(1);
  }, [macroSearch]);

  useEffect(() => {
    if (macroSearch) {
      getFilteredPromptFragments();
    } else {
      getPromptFragments();
    }
  }, [macroSearch, page]);

  const handleScroll = useCallback(
    (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      if (scrollHeight - scrollTop <= clientHeight * 1.5) {
        if (!isFetching && hasMore && !macroSearch) {
          setPage((prev) => prev + 1);
        }
      }
    },
    [isFetching, hasMore, macroSearch]
  );

  useEffect(() => {
    return () => {
      setPage(1);
      setHasMore(true);
    };
  }, []);

  useEffect(() => {
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    };

    setVH();
    window.addEventListener("resize", setVH);
    return () => window.removeEventListener("resize", setVH);
  }, []);

  const macroDropdownStyles = {
    position: "fixed",
    maxHeight: "calc(var(--vh, 1vh) * 32)",
    overflowY: "auto",
    minWidth: "250px",
    backgroundColor: "white",
    zIndex: 1000,
    WebkitOverflowScrolling: "touch",
    msOverflowStyle: "-ms-autohiding-scrollbar",
    overscrollBehavior: "contain",
    webkitTouchCallout: "none",
    webkitUserSelect: "none",
    webkitTapHighlightColor: "transparent",
  };

  return (
    <div
      className="macro-suggestions absolute rounded-md border border-gray-200 bg-white shadow-lg"
      style={{
        ...macroDropdownStyles,
        top: `${dropdownPosition.top}px`,
        left: `${dropdownPosition.left}px`,
        transform:
          dropdownPosition.position === "above" ? "translateY(-100%)" : "none",
        marginTop: dropdownPosition.position === "below" ? "5px" : "0",
        marginBottom: dropdownPosition.position === "above" ? "5px" : "0",
        boxShadow:
          dropdownPosition.position === "above"
            ? `0 -4px 6px -1px ${palette.alpha.black[10]}`
            : `0 4px 6px -1px ${palette.alpha.black[10]}`,
      }}
      onTouchStart={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
      onScroll={handleScroll}
    >
      <div className="max-h-full">
        {fragmentsTableData.map((macro, index) => (
          <div
            key={macro?.id}
            className={`cursor-pointer px-4 py-3 hover:bg-gray-100 active:bg-gray-200 ${
              index === selectedIndex ? "bg-blue-50" : ""
            }`}
            onClick={() => handleMacroSelect(macro)}
            onMouseEnter={() => setSelectedIndex(index)}
            role="option"
            aria-selected={index === selectedIndex}
            tabIndex={0}
          >
            <div className="font-medium">{macro?.key}</div>
            {macro?.value && (
              <div className="text-sm text-gray-500">
                {macro?.value?.length > 50
                  ? `${macro?.value?.slice(0, 50)}...`
                  : macro?.value}
              </div>
            )}
          </div>
        ))}

        {isFetching && (
          <div className="flex min-h-[50px] items-center justify-center py-2">
            <Loader />
          </div>
        )}

        {!hasMore && fragmentsTableData.length > 0 && (
          <div className="py-2 text-center text-sm text-gray-500">
            No more items to load
          </div>
        )}
      </div>
    </div>
  );
}

export default PromptFragmentsMacrosList;
